/**
 * Turning numbers into coordinates. Nothing here decides what a number *is* —
 * that is core's job (T-401) — only where it lands on the page.
 *
 * SVG rather than canvas, per the T-403 note: the PDF report embeds these at
 * print resolution (T-504), and a canvas chart makes that considerably harder.
 */

/**
 * A fixed coordinate system, scaled by CSS.
 *
 * Roughly 1:1 at 375px, which is the width every layout decision here is made
 * for. Using a viewBox rather than measuring the container keeps the chart
 * renderable in a test and in a print stylesheet, neither of which does layout.
 */
export const CHART = {
  width: 360,
  height: 200,
  padLeft: 46,
  padRight: 10,
  padTop: 10,
  padBottom: 30,
} as const

export const PLOT_WIDTH = CHART.width - CHART.padLeft - CHART.padRight
export const PLOT_HEIGHT = CHART.height - CHART.padTop - CHART.padBottom
export const PLOT_RIGHT = CHART.padLeft + PLOT_WIDTH
export const PLOT_BOTTOM = CHART.padTop + PLOT_HEIGHT

export interface Span {
  readonly from: number
  readonly to: number
}

/**
 * Position along the time axis.
 *
 * Time-based rather than one slot per bucket, deliberately: a four-day gap in
 * logging is then four days wide on the page. Evenly spacing whatever buckets
 * happen to exist draws a fortnight of sporadic entries as a tidy regular
 * series, which is a claim about consistency the data does not make.
 */
export function xOf(epochMs: number, span: Span): number {
  const width = span.to - span.from
  if (width <= 0) {
    return CHART.padLeft + PLOT_WIDTH / 2
  }
  const ratio = (epochMs - span.from) / width
  return CHART.padLeft + Math.min(Math.max(ratio, 0), 1) * PLOT_WIDTH
}

/** Position up the value axis. Higher values are higher on the page. */
export function yOf(quantity: number, range: { min: number; max: number }): number {
  const height = range.max - range.min
  if (height <= 0) {
    return CHART.padTop + PLOT_HEIGHT / 2
  }
  const ratio = (quantity - range.min) / height
  return CHART.padTop + (1 - Math.min(Math.max(ratio, 0), 1)) * PLOT_HEIGHT
}

export interface LinePoint {
  readonly x: number
  readonly y: number
  /** Starts a new subpath rather than continuing from the previous point. */
  readonly followsGap: boolean
}

/**
 * An SVG path that breaks at every gap.
 *
 * The acceptance criterion in one function: a period with no data must appear
 * as a break, not as a straight segment across it. A line drawn through a
 * fortnight nobody logged asserts a steady value nobody recorded, and it is
 * indistinguishable from a fortnight of diligent identical readings.
 */
export function linePath(points: readonly LinePoint[]): string {
  let path = ''
  points.forEach((point, index) => {
    const command = index === 0 || point.followsGap ? 'M' : 'L'
    path += `${command}${point.x.toFixed(2)} ${point.y.toFixed(2)} `
  })
  return path.trim()
}

/** Round numbers spanning zero to a maximum, for a count axis. */
export function countTicks(max: number, target = 4): number[] {
  if (max <= 0) {
    return [0]
  }
  const raw = max / target
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step =
    ([1, 2, 5, 10].find((multiple) => multiple * magnitude >= raw) ?? 10) * magnitude
  const ticks: number[] = []
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Number(value.toPrecision(12)))
  }
  return ticks
}

/**
 * The same line, closed down to the baseline so it can be filled.
 *
 * A soft wash under the line is most of what separates a chart that reads as a
 * spreadsheet from one that reads as a picture of a fortnight. The catch is that
 * it is the easiest possible place to reintroduce the lie `linePath` exists to
 * avoid: closing the whole series in one subpath — down, back along the bottom,
 * up — paints straight across every gap, so a fortnight nobody logged arrives as
 * a confident block of colour.
 *
 * So each contiguous run is closed on its own. A gap is a gap in the fill too.
 */
export function areaPath(points: readonly LinePoint[], baseline: number): string {
  const runs: LinePoint[][] = []
  for (const point of points) {
    if (runs.length === 0 || point.followsGap) {
      runs.push([point])
    } else {
      runs[runs.length - 1]!.push(point)
    }
  }

  return runs
    .filter((run) => run.length > 1)
    .map((run) => {
      const first = run[0]!
      const last = run[run.length - 1]!
      const along = run
        .map(
          (point, index) =>
            `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
        )
        .join(' ')
      return `${along} L${last.x.toFixed(2)} ${baseline.toFixed(2)} L${first.x.toFixed(2)} ${baseline.toFixed(2)} Z`
    })
    .join(' ')
}
